/**
 * Predefined Actions Configuration
 *
 * Shortcuts Guidelines:
 * - Use Ctrl+Shift+ for all shortcuts (safe pattern)
 * - Avoids most browser conflicts
 */

export interface PredefinedAction {
  id: string;
  name: string;
  description: string;
  category: 'navigation' | 'actions' | 'general';
  route?: string;
  defaultShortcut?: string;
}

export const PREDEFINED_ACTIONS: PredefinedAction[] = [
  // ============ MOST USED - Quick Access ============
  {
    id: 'nav_home',
    name: 'Loyalty Analytics',
    description: 'Go to main dashboard',
    category: 'navigation',
    route: '/loyalty/analytics',
    defaultShortcut: 'Ctrl+Shift+D',
  },
  {
    id: 'nav_keyboard_shortcuts',
    name: 'Keyboard Shortcuts',
    description: 'View all shortcuts',
    category: 'navigation',
    route: '/keyboard-shortcut',
    defaultShortcut: 'Ctrl+Shift+K',
  },

  // ============ PRODUCTS ============
  {
    id: 'nav_product_list',
    name: 'Products',
    description: 'Open product listing',
    category: 'navigation',
    route: '/product',
    defaultShortcut: 'Ctrl+Shift+P',
  },
  {
    id: 'action_add_product',
    name: 'Add Product',
    description: 'Create a new product',
    category: 'actions',
    route: '/product/add',
    defaultShortcut: 'Alt+Shift+P',
  },

  // ============ USERS ============
  {
    id: 'nav_sales_users',
    name: 'Sales Users',
    description: 'View sales team',
    category: 'navigation',
    route: '/user/sales',
    defaultShortcut: 'Ctrl+Shift+U',
  },
  {
    id: 'action_add_user',
    name: 'Add User',
    description: 'Create a new user',
    category: 'actions',
    route: '/user/sales/add',
    defaultShortcut: 'Alt+Shift+U',
  },
  {
    id: 'nav_backend_staff',
    name: 'Backend Staff',
    description: 'View backend staff',
    category: 'navigation',
    route: '/user/backend',
    defaultShortcut: 'Ctrl+Shift+F',
  },
  {
    id: 'nav_user_hierarchy',
    name: 'User Hierarchy',
    description: 'View reporting structure',
    category: 'navigation',
    route: '/user/hierarchy',
    defaultShortcut: 'Ctrl+Shift+H',
  },

  // ============ CUSTOMERS / CUSTOMERS ============
  {
    id: 'nav_customer',
    name: 'All Customers',
    description: 'View all customers',
    category: 'navigation',
    route: '/customer',
    defaultShortcut: 'Ctrl+Shift+C',
  },
  {
    id: 'nav_primary_network',
    name: 'Distributors',
    description: 'Primary network',
    category: 'navigation',
    route: '/customer/primary-network',
    defaultShortcut: 'Ctrl+Shift+1',
  },
  {
    id: 'nav_secondary_network',
    name: 'Retailers',
    description: 'Secondary network',
    category: 'navigation',
    route: '/customer/secondary-network',
    defaultShortcut: 'Ctrl+Shift+2',
  },
  {
    id: 'nav_direct_network',
    name: 'Sub-Dealers',
    description: 'Direct network',
    category: 'navigation',
    route: '/customer/direct-network',
    defaultShortcut: 'Ctrl+Shift+3',
  },
  {
    id: 'nav_influencer_network',
    name: 'Influencers',
    description: 'Influencer network',
    category: 'navigation',
    route: '/customer/influencer-network',
    defaultShortcut: 'Ctrl+Shift+4',
  },

  // ============ ORDERS ============
  {
    id: 'nav_primary_orders',
    name: 'Primary Orders',
    description: 'View primary orders',
    category: 'navigation',
    route: '/sfa/orders/primary',
    defaultShortcut: 'Ctrl+Shift+O',
  },
  {
    id: 'action_create_primary_order',
    name: 'Create Order',
    description: 'Create new order',
    category: 'actions',
    route: '/sfa/orders/primary/create',
    defaultShortcut: 'Alt+Shift+O',
  },
  {
    id: 'nav_secondary_orders',
    name: 'Secondary Orders',
    description: 'View secondary orders',
    category: 'navigation',
    route: '/sfa/orders/secondary',
    defaultShortcut: 'Ctrl+Shift+5',
  },
  {
    id: 'nav_tertiary_orders',
    name: 'Tertiary Orders',
    description: 'View tertiary orders',
    category: 'navigation',
    route: '/sfa/orders/tertiary',
    defaultShortcut: 'Ctrl+Shift+6',
  },

  // ============ ATTENDANCE & ACTIVITY ============
  {
    id: 'nav_attendance',
    name: 'Attendance',
    description: 'View attendance',
    category: 'navigation',
    route: '/sfa/attendance',
    defaultShortcut: 'Ctrl+Shift+A',
  },
  {
    id: 'nav_attendance_today',
    name: 'Today Attendance',
    description: 'View today\'s attendance',
    category: 'navigation',
    route: '/sfa/attendance/today',
    defaultShortcut: 'Alt+Shift+A',
  },
  {
    id: 'nav_activity',
    name: 'Activities',
    description: 'View activities',
    category: 'navigation',
    route: '/sfa/activity',
    defaultShortcut: 'Ctrl+Shift+Y',
  },

  // ============ LEAVE ============
  {
    id: 'nav_leave',
    name: 'Leave Management',
    description: 'View leave requests',
    category: 'navigation',
    route: '/sfa/leave',
    defaultShortcut: 'Ctrl+Shift+L',
  },
  {
    id: 'action_apply_leave',
    name: 'Apply Leave',
    description: 'Apply for leave',
    category: 'actions',
    route: '/sfa/leave/add',
    defaultShortcut: 'Alt+Shift+L',
  },

  // ============ EXPENSE ============
  {
    id: 'nav_expense',
    name: 'Expenses',
    description: 'View expense claims',
    category: 'navigation',
    route: '/sfa/expense',
    defaultShortcut: 'Ctrl+Shift+X',
  },
  {
    id: 'action_create_expense',
    name: 'Create Expense',
    description: 'Submit expense claim',
    category: 'actions',
    route: '/sfa/expense/add',
    defaultShortcut: 'Alt+Shift+X',
  },

  // ============ TASKS ============
  {
    id: 'nav_tasks',
    name: 'All Tasks',
    description: 'View all tasks',
    category: 'navigation',
    route: '/sfa/task',
    defaultShortcut: 'Ctrl+Shift+T',
  },
  {
    id: 'nav_my_tasks',
    name: 'My Tasks',
    description: 'View your tasks',
    category: 'navigation',
    route: '/sfa/task/my-tasks',
    defaultShortcut: 'Alt+Shift+T',
  },
  {
    id: 'action_add_task',
    name: 'Add Task',
    description: 'Create new task',
    category: 'actions',
    route: '/sfa/task/add',
    defaultShortcut: 'Ctrl+Alt+T',
  },

  // ============ LEADS ============
  {
    id: 'nav_lead',
    name: 'Leads',
    description: 'View leads',
    category: 'navigation',
    route: '/leads',
    defaultShortcut: 'Ctrl+Shift+E',
  },
  {
    id: 'action_add_lead',
    name: 'Add Lead',
    description: 'Create new lead',
    category: 'actions',
    route: '/leads/add',
    defaultShortcut: 'Alt+Shift+E',
  },

  // ============ REPORTS & ANALYTICS ============
  {
    id: 'nav_reports',
    name: 'Reports',
    description: 'View reports',
    category: 'navigation',
    route: '/sfa/reports',
    defaultShortcut: 'Alt+Shift+R',
  },

  // ============ MAP ============
  {
    id: 'nav_map',
    name: 'Map View',
    description: 'View map',
    category: 'navigation',
    route: '/sfa/map',
    defaultShortcut: 'Ctrl+Shift+M',
  },

  // ============ BEAT PLAN ============
  {
    id: 'nav_beat_plan',
    name: 'Beat Plan',
    description: 'View beat plans',
    category: 'navigation',
    route: '/sfa/beat-plan',
    defaultShortcut: 'Ctrl+Shift+B',
  },
  {
    id: 'action_add_beat_plan',
    name: 'Add Beat Plan',
    description: 'Create beat plan',
    category: 'actions',
    route: '/sfa/beat-plan/add',
    defaultShortcut: 'Alt+Shift+B',
  },

  // ============ SUPPORT ============
  {
    id: 'nav_support',
    name: 'Support Tickets',
    description: 'View support tickets',
    category: 'navigation',
    route: '/support',
    defaultShortcut: 'Ctrl+Shift+S',
  },
  {
    id: 'action_add_support',
    name: 'Create Ticket',
    description: 'Create support ticket',
    category: 'actions',
    route: '/support/add',
    defaultShortcut: 'Alt+Shift+S',
  },

  // ============ OTHER MODULES ============
  {
    id: 'nav_announcements',
    name: 'Announcements',
    description: 'View announcements',
    category: 'navigation',
    route: '/announcement',
    defaultShortcut: 'Ctrl+Shift+N',
  },
  {
    id: 'nav_surveys',
    name: 'Surveys',
    description: 'View surveys',
    category: 'navigation',
    route: '/survey',
    defaultShortcut: 'Ctrl+Shift+V',
  },
  {
    id: 'nav_chat',
    name: 'Chat',
    description: 'Open chat',
    category: 'navigation',
    route: '/chat',
    defaultShortcut: 'Ctrl+Shift+G',
  },
  {
    id: 'nav_accounts',
    name: 'Accounts',
    description: 'View accounts',
    category: 'navigation',
    route: '/accounts',
    defaultShortcut: 'Ctrl+Shift+7',
  },

  // ============ POSTAL ============
  {
    id: 'nav_postal',
    name: 'Postal',
    description: 'View postal codes',
    category: 'navigation',
    route: '/postal',
    defaultShortcut: 'Ctrl+Shift+Z',
  },

  // ============ SETTINGS & MASTER DATA ============
  {
    id: 'nav_dropdown_management',
    name: 'Dropdown Management',
    description: 'Manage dropdowns',
    category: 'navigation',
    route: '/dropdown-management',
    defaultShortcut: 'Ctrl+Shift+8',
  },
  {
    id: 'nav_holidays',
    name: 'Holidays',
    description: 'Manage holidays',
    category: 'navigation',
    route: '/holiday',
    defaultShortcut: 'Ctrl+Shift+9',
  },
  {
    id: 'nav_app_config',
    name: 'App Config',
    description: 'App configuration',
    category: 'navigation',
    route: '/app-config',
    defaultShortcut: 'Ctrl+Shift+0',
  },
  {
    id: 'nav_roles',
    name: 'Roles',
    description: 'Manage roles',
    category: 'navigation',
    route: '/authorization/roles',
    defaultShortcut: 'Ctrl+Shift+J',
  },

  // ============ GENERAL ============
  {
    id: 'nav_user_profile',
    name: 'My Profile',
    description: 'View your profile',
    category: 'general',
    route: '/user-profile',
    defaultShortcut: 'Alt+Shift+I',
  },
  {
    id: 'action_logout',
    name: 'Logout',
    description: 'Sign out',
    category: 'general',
    route: '/logout',
    defaultShortcut: 'Ctrl+Shift+Q',
  },
  {
    id: 'action_refresh',
    name: 'Refresh Page',
    description: 'Reload current page',
    category: 'general',
    defaultShortcut: 'Ctrl+Shift+W',
  },
];

export default PREDEFINED_ACTIONS;
